import React from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  Download,
  Eye,
  Mail,
  MousePointerClick,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { AdminReportNav } from "@/components/admin/AdminReportNav";
import { AdminEmptyState, AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { StatCard } from "@/components/ui/StatCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useEmailStatistics } from "@/hooks/useAdminReports";
import { exportEmailStatisticsCsv, type ReportRange } from "@/api/adminReports";
import { cn } from "@/lib/utils";

const rangeLabels: Record<ReportRange, string> = {
  "7d": "7 ημέρες",
  "30d": "30 ημέρες",
  "90d": "90 ημέρες",
  all: "Όλο το διάστημα",
};

const eventTypeMeta = {
  partner: { label: "Συνεργάτης", className: "border-sky-200 bg-sky-50 text-sky-700" },
  lead: { label: "Πελάτης", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  admin: { label: "Admin", className: "border-amber-200 bg-amber-50 text-amber-700" },
};

const clickLabels: Record<string, string> = {
  view: "Προβολή lead",
  mini: "Mini page",
  "status-pending": "Αναλήφθηκε",
  "status-completed": "Ολοκληρώθηκε",
  "status-info": "Πληροφορίες",
};

const formatNumber = (value: number) => new Intl.NumberFormat("el-GR").format(value);

const EmailTrackingStats = () => {
  const [range, setRange] = React.useState<ReportRange>("30d");
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [exporting, setExporting] = React.useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const query = useEmailStatistics(range, page, search);
  const data = query.data;

  const daily = React.useMemo(
    () => (data?.daily ?? []).map((row) => ({
      date: format(parseISO(row.date), "d MMM", { locale: el }),
      sent: Number(row.sent),
      opened: Number(row.opened),
      clicked: Number(row.clicked),
    })),
    [data?.daily],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportEmailStatisticsCsv(range, search);
      toast({ title: "Η εξαγωγή ολοκληρώθηκε", description: "Το CSV περιλαμβάνει όλο το ενεργό φίλτρο, όχι μόνο την τρέχουσα σελίδα." });
    } catch (error) {
      toast({
        title: "Η εξαγωγή απέτυχε",
        description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Στατιστικά email"
        description="Ακριβής εικόνα αποστολών, μετρημένων ανοιγμάτων και clicks από το πλήρες ιστορικό της επιλεγμένης περιόδου."
        icon={<Mail />}
        iconGradient="rose"
        eyebrow="Αναφορές επικοινωνίας"
        actions={
          <>
            <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className={cn("mr-2 h-4 w-4", query.isFetching && "animate-spin")} />
              Ανανέωση
            </Button>
            <Button className="min-h-11 flex-1 bg-slate-950 text-white hover:bg-slate-800 sm:flex-none" onClick={handleExport} disabled={!data || exporting}>
              <Download className={cn("mr-2 h-4 w-4", exporting && "animate-pulse")} />
              Εξαγωγή CSV
            </Button>
          </>
        }
      />

      <AdminReportNav />

      <div className="admin-filter-bar">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Όνομα, email, lead ή tracking ID"
            className="h-11 border-0 bg-slate-50 pl-9 shadow-none ring-1 ring-slate-200 focus-visible:ring-sky-500"
          />
        </div>
        <div className="-mx-1 flex overflow-x-auto px-1 sm:mx-0 sm:ml-auto sm:px-0" aria-label="Περίοδος email reports">
          {(Object.keys(rangeLabels) as ReportRange[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => { setRange(value); setPage(1); }}
              className={cn(
                "min-h-11 shrink-0 rounded-xl px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500",
                range === value ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
              )}
            >
              {rangeLabels[value]}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <AdminMetricSkeletons />
      ) : query.isError || !data ? (
        <AdminErrorState message={query.error instanceof Error ? query.error.message : "Άγνωστο σφάλμα."} onRetry={() => query.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard title="Απεσταλμένα" value={formatNumber(data.summary.sent)} icon={<Send />} variant="info" description={`${data.summary.failed} αποτυχίες`} />
            <StatCard title="Επιτυχία αποστολής" value={`${data.summary.delivery_rate}%`} icon={<ShieldCheck />} variant={data.summary.delivery_rate >= 98 ? "success" : "warning"} description="Αποδοχή SMTP, όχι παράδοση inbox" />
            <StatCard title="Μετρημένα ανοίγματα" value={`${data.summary.open_rate}%`} icon={<Eye />} variant="success" description={`${formatNumber(data.summary.opened)} emails`} />
            <StatCard title="Click rate" value={`${data.summary.click_rate}%`} icon={<MousePointerClick />} variant="warning" description={`${formatNumber(data.summary.clicked)} emails με click`} />
          </div>

          <Tabs defaultValue="overview" className="space-y-5">
            <div className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
              <TabsList className="h-11 min-w-max rounded-xl bg-slate-200/70 p-1">
                <TabsTrigger value="overview" className="min-h-9 rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm">Επισκόπηση</TabsTrigger>
                <TabsTrigger value="events" className="min-h-9 rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm">Αποστολές ({data.pagination.total})</TabsTrigger>
                <TabsTrigger value="partners" className="min-h-9 rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm">Συνεργάτες</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="mt-0 space-y-5">
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.85fr)]">
                <Card className="admin-panel">
                  <CardHeader className="pb-2">
                    <CardTitle className="admin-section-title">Δραστηριότητα email</CardTitle>
                    <CardDescription>Οι αλληλεπιδράσεις αποδίδονται στην ημερομηνία αποστολής.</CardDescription>
                  </CardHeader>
                  <CardContent className="h-[310px] pl-1 pr-3 sm:h-[360px] sm:pl-3">
                    {daily.length === 0 ? (
                      <AdminEmptyState title="Δεν υπάρχουν αποστολές" description="Δεν βρέθηκαν emails στην επιλεγμένη περίοδο." illustration="stage-reminders" />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={daily} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
                          <defs>
                            <linearGradient id="emailSent" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#0369a1" stopOpacity={0.18} />
                              <stop offset="95%" stopColor="#0369a1" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" minTickGap={24} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(15,23,42,.08)" }} />
                          <Area type="monotone" dataKey="sent" name="Απεσταλμένα" stroke="#0369a1" strokeWidth={2} fill="url(#emailSent)" />
                          <Area type="monotone" dataKey="opened" name="Ανοίγματα" stroke="#059669" strokeWidth={2} fillOpacity={0} />
                          <Area type="monotone" dataKey="clicked" name="Clicks" stroke="#d97706" strokeWidth={2} fillOpacity={0} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <div className="space-y-5">
                  <Card className="admin-panel">
                    <CardHeader className="pb-3">
                      <CardTitle className="admin-section-title">Διαδρομή engagement</CardTitle>
                      <CardDescription>Μοναδικά emails, όχι raw events.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {[
                        { label: "Απεσταλμένα", value: data.summary.sent, pct: 100, color: "bg-sky-700" },
                        { label: "Με άνοιγμα", value: data.summary.opened, pct: data.summary.open_rate, color: "bg-emerald-600" },
                        { label: "Με click", value: data.summary.clicked, pct: data.summary.click_rate, color: "bg-amber-600" },
                      ].map((item) => (
                        <div key={item.label}>
                          <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                            <span className="text-slate-600">{item.label}</span>
                            <span className="font-semibold tabular-nums text-slate-900">{formatNumber(item.value)} · {item.pct}%</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div className={cn("h-full rounded-full", item.color)} style={{ width: `${Math.min(100, item.pct)}%` }} />
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-500 ring-1 ring-slate-100">
                        <Clock3 className="h-4 w-4 shrink-0 text-slate-400" />
                        Μέσος χρόνος έως άνοιγμα: <strong className="text-slate-700">{data.summary.avg_open_hours} ώρες</strong>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="admin-panel">
                    <CardHeader className="pb-3">
                      <CardTitle className="admin-section-title">Clicks ανά ενέργεια</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2.5">
                      {data.click_types.length === 0 ? (
                        <p className="py-4 text-center text-sm text-slate-400">Δεν καταγράφηκαν clicks.</p>
                      ) : data.click_types.map((item) => (
                        <div key={item.name} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                          <span className="text-sm text-slate-600">{clickLabels[item.name] || item.name}</span>
                          <span className="font-semibold tabular-nums text-slate-900">{item.total}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-sky-100 bg-sky-50/70 p-4 text-sm leading-6 text-sky-900">
                <Eye className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
                <p><strong>Σωστή ερμηνεία:</strong> τα ανοίγματα email είναι ενδεικτικά, επειδή image blocking και privacy prefetch μπορούν να τα υπομετρήσουν ή να τα υπερμετρήσουν. Τα clicks είναι το ισχυρότερο engagement signal.</p>
              </div>
            </TabsContent>

            <TabsContent value="events" className="mt-0">
              <Card className="admin-panel overflow-hidden">
                {data.events.length === 0 ? (
                  <AdminEmptyState title="Δεν βρέθηκαν αποστολές" description="Δοκιμάστε άλλη περίοδο ή καθαρίστε την αναζήτηση." illustration="stage-reminders" />
                ) : (
                  <>
                    <div className="divide-y divide-slate-100 md:hidden">
                      {data.events.map((event) => {
                        const meta = eventTypeMeta[event.email_type];
                        return (
                          <article key={event.tracking_id} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-900">{event.partner_name || event.lead_name || event.email || "Άγνωστος παραλήπτης"}</p>
                                <p className="mt-1 truncate text-xs text-slate-500">{event.email || "Χωρίς email"}</p>
                              </div>
                              <Badge variant="outline" className={cn("shrink-0 text-[11px]", meta.className)}>{meta.label}</Badge>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-center">
                              <div><p className="text-[10px] uppercase text-slate-400">Αποστολή</p><p className="mt-1 text-xs font-semibold text-slate-700">{event.sent_at ? format(new Date(event.sent_at), "dd/MM HH:mm") : "—"}</p></div>
                              <div><p className="text-[10px] uppercase text-slate-400">Άνοιγμα</p><p className={cn("mt-1 text-xs font-semibold", event.opened_at ? "text-emerald-700" : "text-slate-400")}>{event.opened_at ? "Ναι" : "Όχι"}</p></div>
                              <div><p className="text-[10px] uppercase text-slate-400">Clicks</p><p className="mt-1 text-xs font-semibold text-amber-700">{event.clicks}</p></div>
                            </div>
                            {event.lead_id && <Link to={`/admin/lead/${event.lead_id}`} className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-sky-700">Άνοιγμα lead <ArrowRight className="ml-1.5 h-4 w-4" /></Link>}
                          </article>
                        );
                      })}
                    </div>

                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                          <tr><th className="px-4 py-3">Παραλήπτης</th><th className="px-4 py-3">Lead</th><th className="px-4 py-3">Αποστολή</th><th className="px-4 py-3">Άνοιγμα</th><th className="px-4 py-3 text-right">Clicks</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {data.events.map((event) => {
                            const meta = eventTypeMeta[event.email_type];
                            return (
                              <tr key={event.tracking_id} className="hover:bg-slate-50/70">
                                <td className="px-4 py-3"><div className="flex items-center gap-2"><Badge variant="outline" className={cn("text-[10px]", meta.className)}>{meta.label}</Badge><div className="min-w-0"><p className="max-w-56 truncate font-medium text-slate-900">{event.partner_name || event.email || "—"}</p><p className="max-w-56 truncate text-xs text-slate-400">{event.email}</p></div></div></td>
                                <td className="px-4 py-3">{event.lead_id ? <Link to={`/admin/lead/${event.lead_id}`} className="font-medium text-sky-700 hover:underline">{event.lead_name || event.lead_id.slice(0, 8)}</Link> : <span className="text-slate-400">—</span>}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{event.sent_at ? format(new Date(event.sent_at), "dd/MM/yyyy HH:mm") : "—"}</td>
                                <td className="px-4 py-3">{event.opened_at ? <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />Ναι</span> : <span className="text-slate-400">Όχι</span>}</td>
                                <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">{event.clicks}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-slate-500">Σελίδα {data.pagination.page} από {data.pagination.pages} · {formatNumber(data.pagination.total)} αποστολές</p>
                      <div className="flex gap-2">
                        <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" disabled={page <= 1 || query.isFetching} onClick={() => setPage((current) => Math.max(1, current - 1))}><ArrowLeft className="mr-2 h-4 w-4" />Προηγούμενη</Button>
                        <Button variant="outline" className="min-h-11 flex-1 sm:flex-none" disabled={page >= data.pagination.pages || query.isFetching} onClick={() => setPage((current) => Math.min(data.pagination.pages, current + 1))}>Επόμενη<ArrowRight className="ml-2 h-4 w-4" /></Button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="partners" className="mt-0">
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(340px,0.8fr)]">
                <Card className="admin-panel">
                  <CardHeader className="pb-2"><CardTitle className="admin-section-title">Emails ανά συνεργάτη</CardTitle><CardDescription>Οι 12 μεγαλύτεροι όγκοι αποστολών.</CardDescription></CardHeader>
                  <CardContent className="h-[380px] pl-1 pr-3 sm:pl-3">
                    {data.partners.length === 0 ? <AdminEmptyState title="Δεν υπάρχουν δεδομένα συνεργατών" description="Δεν βρέθηκαν partner emails στην περίοδο." illustration="partner-network" /> : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.partners.slice(0, 12)} layout="vertical" margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
                          <CartesianGrid horizontal={false} stroke="#e2e8f0" strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                          <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(value) => value.length > 16 ? `${value.slice(0, 16)}…` : value} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                          <Bar dataKey="sent" name="Απεσταλμένα" fill="#0369a1" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="opened" name="Ανοίγματα" fill="#059669" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card className="admin-panel overflow-hidden">
                  <CardHeader className="pb-3"><CardTitle className="admin-section-title">Engagement δικτύου</CardTitle><CardDescription>Ταξινόμηση ανά όγκο αποστολών.</CardDescription></CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[410px] divide-y divide-slate-100 overflow-y-auto">
                      {data.partners.map((partner) => (
                        <div key={partner.id} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-xs font-bold text-slate-600"><Users className="h-4 w-4" /></div>
                          <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-slate-900">{partner.name}</p><p className="text-xs text-slate-500">{partner.sent} emails · {partner.clicked} με click</p></div>
                          <span className="text-sm font-bold tabular-nums text-emerald-700">{partner.open_rate}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default EmailTrackingStats;
